import apiClient from '../apiClient';

// Types and Interfaces
export interface HospitalConfig {
  name: { en: string; ar: string };
  tagline: { en: string; ar: string };
  address: {
    street: { en: string; ar: string };
    city: { en: string; ar: string };
    state: { en: string; ar: string };
    country: { en: string; ar: string };
    zipCode: string;
  };
  contact: {
    phone: string;
    fax?: string;
    email: string;
    website?: string;
  };
  license: {
    number: string;
    issuedBy: string;
    validUntil: string;
  };
  defaultLocale: 'en' | 'ar';
  /** Per-tenant billing display currency (ISO-4217, e.g. EGP/SAR/USD).
   *  Persisted server-side so the choice survives across sessions and
   *  devices; the EMR seeds its currency cache from this on load. */
  billingCurrency?: string;
  /** ISO 3166-1 alpha-2 a phone typed without its code belongs to (default EG). */
  defaultCountry?: string;
  branding: {
    hospitalName: string;
    tagline: string;
    aiProvider: string;
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    logoPosition: 'left' | 'center' | 'right';
    headerStyle: 'simple' | 'gradient' | 'image';
    // Mirrored to tenant_brandings.logoUrl on save so the auth page
    // (which reads tenant_brandings via /public/tenant-config) and the
    // EMR sidebar (which reads hospital.config via this endpoint) stay
    // pointing at the same image.
    logoUrl?: string | null;
    // Picked theme id + full palette. Saving both lets the frontend
    // apply the exact theme (palette) the OWNER chose to every user's
    // session, instead of each device defaulting to its own local
    // defaultTheme. `themeConfig` is a serialized `ThemeConfig`.
    themeId?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    themeConfig?: any;
  };
}

export interface SystemConfig {
  dataRetention: {
    patientRecords: number; // months
    auditLogs: number; // months
    backups: number; // months
  };
  security: {
    sessionTimeout: number; // minutes
    passwordExpiry: number; // days
    loginAttempts: number;
    requireMFA: boolean;
    allowedIPs: string[];
  };
  notifications: {
    emailEnabled: boolean;
    smsEnabled: boolean;
    pushEnabled: boolean;
    alertThresholds: {
      systemLoad: number;
      diskUsage: number;
      memoryUsage: number;
    };
  };
  backup: {
    autoBackup: boolean;
    frequency: 'daily' | 'weekly' | 'monthly';
    retentionDays: number;
    location: 'local' | 'cloud' | 'both';
  };
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// API Functions

/**
 * Get hospital configuration (public - no authentication required)
 * This endpoint is accessible without authentication for publicly available settings
 */
export const getHospitalConfig = async (): Promise<ApiResponse<HospitalConfig>> => {
  const response = await apiClient.get<ApiResponse<HospitalConfig>>(
    '/system-settings/frontend/hospital-config'
  );
  return response.data;
};

/**
 * Create hospital configuration (requires authentication)
 */
export const createHospitalConfig = async (
  data: HospitalConfig
): Promise<ApiResponse<HospitalConfig>> => {
  const response = await apiClient.post<ApiResponse<HospitalConfig>>(
    '/system-settings/frontend/hospital-config',
    data
  );
  return response.data;
};

/**
 * Update hospital configuration (requires authentication)
 */
export const updateHospitalConfig = async (
  data: HospitalConfig
): Promise<ApiResponse<HospitalConfig>> => {
  const response = await apiClient.put<ApiResponse<HospitalConfig>>(
    '/system-settings/frontend/hospital-config',
    data
  );
  return response.data;
};

/**
 * Get system configuration (requires authentication)
 */
export const getSystemConfig = async (): Promise<ApiResponse<SystemConfig>> => {
  const response = await apiClient.get<ApiResponse<SystemConfig>>(
    '/system-settings/frontend/system-config'
  );
  return response.data;
};

/**
 * Create system configuration (requires authentication)
 */
export const createSystemConfig = async (
  data: SystemConfig
): Promise<ApiResponse<SystemConfig>> => {
  const response = await apiClient.post<ApiResponse<SystemConfig>>(
    '/system-settings/frontend/system-config',
    data
  );
  return response.data;
};

/**
 * Update system configuration (requires authentication)
 */
export const updateSystemConfig = async (
  data: SystemConfig
): Promise<ApiResponse<SystemConfig>> => {
  const response = await apiClient.put<ApiResponse<SystemConfig>>(
    '/system-settings/frontend/system-config',
    data
  );
  return response.data;
};

export interface RecordingLimits {
  /** Hard cap (minutes) on one recording session. */
  maxRecordingMinutes: number;
  /** Sustained-silence auto-finalize (minutes). 0 = disabled. */
  silenceTimeoutMinutes: number;
}

/**
 * Per-tenant recording limits (any authenticated user in the tenant).
 */
export const getRecordingLimits = async (): Promise<
  ApiResponse<RecordingLimits>
> => {
  const response = await apiClient.get<ApiResponse<RecordingLimits>>(
    '/system-settings/frontend/recording-limits'
  );
  return response.data;
};

/**
 * Update per-tenant recording limits (admin only). Server clamps the values;
 * the response carries the effective (clamped) limits.
 */
export const updateRecordingLimits = async (
  data: RecordingLimits
): Promise<ApiResponse<RecordingLimits>> => {
  const response = await apiClient.put<ApiResponse<RecordingLimits>>(
    '/system-settings/frontend/recording-limits',
    data
  );
  return response.data;
};

/**
 * Which modules this clinic has switched on. Keys are `ModuleKey` values;
 * an absent key means "never chosen", which the store resolves to its
 * default rather than to `false`.
 *
 * This is the clinic's PREFERENCE, not its entitlement — the subscription
 * still decides what it may have at all. See useFeatureToggleStore.
 */
export type ModuleSettings = Record<string, boolean>;

/** Readable by any authenticated user — it drives what their EMR renders. */
export const getModuleSettings = async (): Promise<
  ApiResponse<ModuleSettings>
> => {
  const response = await apiClient.get<ApiResponse<ModuleSettings>>(
    '/system-settings/frontend/modules'
  );
  return response.data;
};

/**
 * Replace the clinic's module choices (system:write). Full-set write —
 * send the complete map, since an omitted key reads as absent, not as
 * unchanged.
 */
export const updateModuleSettings = async (
  data: ModuleSettings
): Promise<ApiResponse<ModuleSettings>> => {
  const response = await apiClient.put<ApiResponse<ModuleSettings>>(
    '/system-settings/frontend/modules',
    data
  );
  return response.data;
};

/**
 * Per-tenant patient ID (MRN) generation format. `prefix` leads every
 * auto-generated code; `strategy` picks a readable running number or a
 * random short code. Distinct from the system UUID — this is the code
 * staff search by and print on invoices.
 */
export interface PatientIdFormat {
  prefix: string;
  strategy: 'sequential' | 'random';
}

export const getPatientIdFormat = async (): Promise<
  ApiResponse<PatientIdFormat>
> => {
  const response = await apiClient.get<ApiResponse<PatientIdFormat>>(
    '/system-settings/frontend/patient-id-format'
  );
  return response.data;
};

/**
 * Update the patient ID format (admin only). Server validates the prefix;
 * the response carries the effective values. Existing patients are not
 * renumbered — only new patients draw from the new format.
 */
export const updatePatientIdFormat = async (
  data: PatientIdFormat
): Promise<ApiResponse<PatientIdFormat>> => {
  const response = await apiClient.put<ApiResponse<PatientIdFormat>>(
    '/system-settings/frontend/patient-id-format',
    data
  );
  return response.data;
};

// Export all functions as a default object for convenience
const systemSettingsApi = {
  getHospitalConfig,
  createHospitalConfig,
  updateHospitalConfig,
  getSystemConfig,
  createSystemConfig,
  updateSystemConfig,
  getRecordingLimits,
  updateRecordingLimits,
  getPatientIdFormat,
  updatePatientIdFormat,
};

export default systemSettingsApi;
